import { Link, Outlet, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { MuralResumo, NotificacoesResposta } from '@sgc/contracts';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DuasEtapas } from '../telas/seguranca/duas-etapas';
import { AceiteDeTermos, EscritorioBloqueado } from '../telas/termos/termos';
import { Icone, type NomeIcone } from '../ui/icones';
import { registrarSwEquipe } from '../lib/push-equipe';
import { BuscaGlobal } from '../ui/busca-global';

export const Route = createFileRoute('/_app')({
  beforeLoad: ({ context, location }) => {
    if (context.auth.setupNecessario) throw redirect({ to: '/setup' });
    if (!context.auth.eu) throw redirect({ to: '/login', search: { proximo: location.href } as never });
  },
  component: Shell,
});

const MENU: Array<{ grupo: string; itens: Array<{ to: string; ic: NomeIcone; rotulo: string }> }> = [
  { grupo: 'Dia a dia', itens: [
    { to: '/', ic: 'painel', rotulo: 'Painel' },
    { to: '/tarefas', ic: 'tarefas', rotulo: 'Tarefas' },
    { to: '/guias', ic: 'nota', rotulo: 'Guias' },
    { to: '/notificacoes', ic: 'sino', rotulo: 'Notificações' },
    { to: '/mural', ic: 'megafone', rotulo: 'Mural' },
    { to: '/relatorios', ic: 'grafico', rotulo: 'Relatórios' },
  ] },
  { grupo: 'Clientes', itens: [
    { to: '/clientes', ic: 'empresa', rotulo: 'Clientes' },
    { to: '/pedidos', ic: 'lista', rotulo: 'Pedidos' },
    { to: '/processos', ic: 'fluxo', rotulo: 'Processos' },
    { to: '/vencimentos', ic: 'relogio', rotulo: 'Vencimentos' },
    { to: '/cofre', ic: 'chave', rotulo: 'Cofre' },
    { to: '/pessoal', ic: 'pessoa', rotulo: 'Pessoal' },
    { to: '/documentos', ic: 'arquivo', rotulo: 'Gerador de documentos' },
    { to: '/gestao', ic: 'maleta', rotulo: 'Gestão da carteira' },
  ] },
  { grupo: 'Receita e fiscal', itens: [
    { to: '/sicalc', ic: 'dinheiro', rotulo: 'Emissor DARF' },
    { to: '/consultas', ic: 'busca', rotulo: 'Consultas RFB' },
    { to: '/situacao-fiscal', ic: 'pulso', rotulo: 'Situação fiscal' },
    { to: '/reforma', ic: 'bussola', rotulo: 'Reforma tributária' },
  ] },
  { grupo: 'Cadastros', itens: [
    { to: '/obrigacoes', ic: 'nota', rotulo: 'Obrigações' },
    { to: '/modelos-tarefa', ic: 'pastas', rotulo: 'Cadastro de tarefas' },
    { to: '/fluxos', ic: 'fluxo', rotulo: 'Fluxos' },
    { to: '/servicos', ic: 'sino2', rotulo: 'Serviços' },
    { to: '/feriados', ic: 'calendario', rotulo: 'Feriados' },
  ] },
  { grupo: 'Escritório', itens: [
    { to: '/equipe', ic: 'pessoas', rotulo: 'Equipe' },
    { to: '/configuracoes', ic: 'engrenagem', rotulo: 'Configurações' },
    { to: '/emails', ic: 'enviar', rotulo: 'Envios e e-mails' },
    { to: '/auditoria', ic: 'historico', rotulo: 'Auditoria' },
    { to: '/ajuda', ic: 'ajuda', rotulo: 'Como usar' },
  ] },
];

/** só o super administrador da plataforma vê (quem vende o SGC a outros escritórios) */
const MENU_PLATAFORMA: (typeof MENU)[number] = { grupo: 'Plataforma', itens: [{ to: '/plataforma', ic: 'predio', rotulo: 'Escritórios' }] };

function Shell() {
  const auth = useAuth();
  const nav = useNavigate();
  const eu = auth.eu!;
  const notifs = useQuery({ queryKey: ['notificacoes', 'contador'], queryFn: () => get<NotificacoesResposta>('/v1/notificacoes', { naoLidas: true }), refetchInterval: 60_000 });
  const naoLidas = notifs.data?.naoLidas ?? 0;
  const mural = useQuery({ queryKey: ['mural', 'resumo'], queryFn: () => get<MuralResumo>('/v1/mural/resumo'), refetchInterval: 120_000, enabled: !eu.precisaConfigurar2fa });
  const recadosNovos = mural.data?.naoLidos ?? 0;
  const [menu, setMenu] = useState(false);
  useEffect(() => { setMenu(false); }, [nav]);
  const sair = () => { void auth.sair().then(() => nav({ to: '/login' })); };
  // nenhum escritório abre (suspenso ou teste vencido): explica, em vez de uma tela em que tudo dá erro
  if (!eu.escritorio && eu.bloqueios?.length) return <EscritorioBloqueado bloqueios={eu.bloqueios} onSair={sair} />;
  // escritório criado pela plataforma: aceite dos termos antes de qualquer coisa
  if (eu.termosPendentes) return <AceiteDeTermos pendente={eu.termosPendentes} escritorio={eu.escritorio?.nome ?? null} onSair={sair} />;
  const diasDeTeste = eu.escritorio?.testeAte ? Math.ceil((new Date(`${eu.escritorio.testeAte}T23:59:59`).getTime() - Date.now()) / 86_400_000) : null;
  // o escritório exige duas etapas e este usuário ainda não ligou: a API só responde à ativação, a tela também
  if (eu.precisaConfigurar2fa) return (
    <div className="main" style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="topbar"><div><h1>Falta um passo</h1><div className="sub">{eu.escritorio?.nome} exige a verificação em duas etapas de toda a equipe</div></div><div className="acoes"><button className="btn" type="button" onClick={() => { void auth.sair().then(() => nav({ to: '/login' })); }}>Sair</button></div></div>
      <div className="conteudo"><DuasEtapas obrigatorio /></div>
    </div>
  );
  // o service worker do escritório só existe para as notificações; registrar cedo deixa o botão de avisos pronto
  useEffect(() => { void registrarSwEquipe(); }, []);
  const [busca, setBusca] = useState(false);
  useEffect(() => {
    const f = (e: KeyboardEvent) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setBusca(true); } };
    window.addEventListener('keydown', f); return () => window.removeEventListener('keydown', f);
  }, []);
  return (
    <div className={`shell${menu ? ' menu-aberto' : ''}`}>
      <header className="barra-movel">
        <button className="btn icone" type="button" aria-label="menu" onClick={() => setMenu((m) => !m)}>☰</button>
        <span className="logo-mark">SGC</span><b title={eu.escritorio?.nome ?? undefined}>{eu.escritorio?.nome ?? 'SGC'}</b>
        <button className="btn icone" type="button" aria-label="buscar" onClick={() => setBusca(true)}><Icone n="busca" /></button>
        <Link to="/notificacoes" className="sino" aria-label="notificações">🔔{naoLidas > 0 && <span className="badge">{naoLidas}</span>}</Link>
      </header>
      {menu && <div className="menu-fundo" onClick={() => setMenu(false)} />}
      <aside className="sidebar" onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setMenu(false); }}>
        <div className="sidebar-brand">
          <span className="logo-mark">SGC</span>
          <div>SGC<span className="sub">{eu.escritorio?.nome ?? 'sem escritório'}</span></div>
        </div>
        <button className="busca-botao" type="button" onClick={() => setBusca(true)} title="buscar cliente, tarefa, documento ou pedido (Ctrl+K)"><Icone n="busca" /> Buscar… <kbd>Ctrl K</kbd></button>
        <BuscaGlobal aberta={busca} onFechar={() => setBusca(false)} />
        <nav className="sidebar-nav">
          {(eu.superAdmin ? [...MENU, MENU_PLATAFORMA] : MENU).map((g) => (
            <div key={g.grupo}>
              <div className="grupo">{g.grupo}</div>
              {g.itens.map((it) => (
                <Link key={it.to} to={it.to} activeOptions={{ exact: it.to === '/' }}>
                  <span className="ic"><Icone n={it.ic} /></span>{it.rotulo}{it.to === '/notificacoes' && naoLidas > 0 && <span className="badge">{naoLidas}</span>}{it.to === '/mural' && recadosNovos > 0 && <span className="badge">{recadosNovos}</span>}
                </Link>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-foot">
          <div className="nome">{eu.nome}</div>
          <div className="email" title={eu.email}>{eu.email}</div>
          {eu.escritorios.length > 1 && (
            <select value={eu.escritorio?.id ?? ''} onChange={(e) => void auth.trocarEscritorio(e.target.value)}>
              {eu.escritorios.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          )}
          <div className="acoes">
            <button className="btn mini" type="button" onClick={() => { void auth.sair().then(() => nav({ to: '/login' })); }}>Sair</button>
          </div>
        </div>
      </aside>
      <div className="main">
        {diasDeTeste != null && diasDeTeste <= 7 && <div className="alerta atencao" style={{ margin: '10px 24px 0' }}>O período de teste deste escritório termina {diasDeTeste <= 0 ? 'hoje' : diasDeTeste === 1 ? 'amanhã' : `em ${diasDeTeste} dias`}. Para continuar sem interrupção, fale com o suporte do SGC.</div>}
        <Outlet />
      </div>
    </div>
  );
}
